/* ============================================================
   PILIH WORKFLOW KAYA — butang + menu "dropdown kaya" (3 Nov 2026)
   ============================================================
   Rupa lakaran 1 yang dipilih user: tertutup = gambar aliran + nama + ayat pendek;
   dibuka = senarai dikumpul ikut kategori (KATEGORI_WF), setiap baris gambar aliran +
   nama + ayat + tag, tanda Draft. Dikongsi oleh:
     · WorkflowPilihV39   — prototaip /workspace-v3.9 (data contoh)
     · WorkflowDropdownV4 — Control Panel Workspace v4 mod New (data page /workflow)
   Komponen RUPA sahaja: tiada simpanan, tiada Firestore. Menu diportal ke <body> atas
   sebab sama seperti PilihV39.jsx (kad panel `overflow:hidden` mengerat menu).

   `pilihan` = workflow bentuk utils/galeriWorkflow.js (nama · penerangan · tag · ilustrasi ·
   status · kategori). `ganti` (opsyen) = isi butang tertutup bila tiada satu workflow untuk
   dipapar (cth "Tick a card first" / "2 workflows").
   ============================================================ */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { Badge } from '../design/ui';
import { useT } from '../i18n';
import GambarAliranWf from './GambarAliranWf';
import { KATEGORI_WF, ilustrasiWf } from '../utils/galeriWorkflow';
import './WorkflowV39.css';
import './WorkflowPilihV39.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/* Gambar aliran panjang (cth "gv|gv|gv") dikecilkan supaya tidak melimpah. */
const saizArt = (id, asas) => (ilustrasiWf(id).token.length > 4 ? asas - 3 : asas);

export default function PilihWorkflowKaya({ pilihan = [], nilai, onPilih, disabled = false, ganti = null, kakiMenu = null }) {
    const t = useT();
    const [buka, setBuka] = useState(false);
    const [pos, setPos] = useState(null);
    const rujuk = useRef(null);
    const menuRujuk = useRef(null);

    const kini = pilihan.find((w) => w.id === nilai) || null;
    /* Murah (≤100 item) — dikira terus setiap render; useMemo di sini ditolak React Compiler. */
    const kumpulan = KATEGORI_WF
        .map((k) => ({ ...k, isi: pilihan.filter((w) => w.kategori === k.id) }))
        .filter((k) => k.isi.length > 0);

    const kiraPos = useCallback(() => {
        const btn = rujuk.current;
        if (!btn) return;
        const r = btn.getBoundingClientRect();
        const jurang = 6;
        const tepi = 8;
        const ruangBawah = window.innerHeight - r.bottom - tepi;
        const ruangAtas = r.top - tepi;
        const keAtas = ruangBawah < 260 && ruangAtas > ruangBawah;
        const ruang = (keAtas ? ruangAtas : ruangBawah) - jurang;
        const lebar = Math.max(r.width, 300);
        setPos({
            top: keAtas ? 'auto' : Math.round(r.bottom + jurang),
            bottom: keAtas ? Math.round(window.innerHeight - r.top + jurang) : 'auto',
            left: Math.round(Math.max(tepi, Math.min(r.left, window.innerWidth - lebar - tepi))),
            width: lebar,
            maxHeight: Math.max(160, Math.min(440, ruang)),
        });
    }, []);

    useEffect(() => {
        if (!buka) return undefined;
        const luar = (e) => {
            if (rujuk.current?.contains(e.target) || menuRujuk.current?.contains(e.target)) return;
            setBuka(false);
        };
        const esc = (e) => { if (e.key === 'Escape') setBuka(false); };
        const semula = () => kiraPos();
        document.addEventListener('mousedown', luar);
        document.addEventListener('keydown', esc);
        window.addEventListener('resize', semula);
        window.addEventListener('scroll', semula, true);
        return () => {
            document.removeEventListener('mousedown', luar);
            document.removeEventListener('keydown', esc);
            window.removeEventListener('resize', semula);
            window.removeEventListener('scroll', semula, true);
        };
    }, [buka, kiraPos]);

    /* Menu tidak boleh kekal bila kawalan dilumpuhkan / pilihan habis — dikira, bukan disimpan. */
    const nampakMenu = buka && !disabled && pilihan.length > 0;

    const togol = () => {
        if (buka) { setBuka(false); return; }
        kiraPos();
        setBuka(true);
    };

    const tandaDraft = (w) => (w.status === 'draft'
        ? <Badge tone="warn" style={{ fontSize: 10.5, padding: '0 6px' }}>{t('workspace.wfPilih.draft')}</Badge>
        : null);

    return (
        <div ref={rujuk} style={{ position: 'relative' }}>
            <button
                type="button"
                className="s-pick wfp-btn"
                aria-haspopup="listbox"
                aria-expanded={nampakMenu}
                aria-label={t('workspace.wfPilih.aria')}
                disabled={disabled}
                onClick={togol}
            >
                {kini && !ganti ? (
                    <span className="wfp-teks">
                        <GambarAliranWf ilustrasi={kini.ilustrasi} saiz={saizArt(kini.ilustrasi, 15)} className="wfg-art wfg-art--kecil wfp-art" />
                        <span className="wfp-nama">{kini.nama} {tandaDraft(kini)}</span>
                        {kini.penerangan && <span className="wfp-nota">{kini.penerangan}</span>}
                    </span>
                ) : (
                    <span className="wfp-teks">
                        {ganti || <span className="wfp-nota">{t('workspace.wfPilih.kosong')}</span>}
                    </span>
                )}
                <ChevronDown size={14} strokeWidth={2} style={{ flex: 'none', color: 'var(--s-ink-3)' }} />
            </button>

            {nampakMenu && pos && createPortal(
                <div
                    ref={menuRujuk}
                    className="s-pop wfp-menu"
                    role="listbox"
                    aria-label={t('workspace.wfPilih.aria')}
                    style={{
                        position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left,
                        width: pos.width, maxWidth: 'calc(100vw - 16px)', maxHeight: pos.maxHeight, zIndex: 10040,
                    }}
                >
                    {kumpulan.map((k, i) => (
                        <div key={k.id} role="group" aria-label={k.label}>
                            {i > 0 && <span className="s-pop__sep" />}
                            <p className="wfp-kumpulan">{k.label}</p>
                            {k.isi.map((w) => {
                                const aktif = w.id === nilai;
                                return (
                                    <button
                                        key={w.id}
                                        type="button"
                                        role="option"
                                        aria-selected={aktif}
                                        className={cx('s-pop__row', 'wfp-baris', aktif && 'is-on')}
                                        onClick={() => { setBuka(false); onPilih?.(w); }}
                                    >
                                        <span className="wfp-teks">
                                            <GambarAliranWf ilustrasi={w.ilustrasi} saiz={saizArt(w.ilustrasi, 14)} className="wfg-art wfg-art--kecil wfp-art" />
                                            <span className="wfp-nama">{w.nama} {tandaDraft(w)}</span>
                                            {w.penerangan && <span className="wfp-nota">{w.penerangan}</span>}
                                            {w.tag?.length > 0 && <span className="wfp-tag">{w.tag.join(' · ')}</span>}
                                        </span>
                                        {aktif && <Check size={13} strokeWidth={2.2} style={{ flex: 'none', color: 'var(--s-ink)', marginTop: 2 }} />}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                    {kakiMenu && <p className="wfp-kaki">{kakiMenu}</p>}
                </div>,
                document.body,
            )}
        </div>
    );
}
